import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../../config/runtime';
import './ClientManager.css';

const ClientManager = () => {
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    document: '',
    notes: '',
    tags: '',
    company_id: '',
  });
  const [linkForm, setLinkForm] = useState({ chatId: '', phone: '' });

  const loadClients = async () => {
    try {
      const res = await axios.get(`${apiBaseUrl}/customers`);
      setClients(res.data || []);
    } catch (error) {}
  };

  const loadHistory = async (customerId) => {
    try {
      const res = await axios.get(`${apiBaseUrl}/customers/${customerId}/history`);
      setHistory(res.data || []);
    } catch (error) {}
  };

  const loadCompanies = async () => {
    try {
      const res = await axios.get(`${apiBaseUrl}/companies`);
      setCompanies(res.data || []);
    } catch (error) {
      setCompanies([]);
    }
  };

  useEffect(() => {
    loadClients();
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        company_id: form.company_id || null,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean)
      };
      if (selected) {
        await axios.put(`${apiBaseUrl}/customers/${selected.id}`, payload);
      } else {
        await axios.post(`${apiBaseUrl}/customers`, payload);
      }
      setForm({ name: '', phone: '', email: '', document: '', notes: '', tags: '', company_id: '' });
      setSelected(null);
      loadClients();
    } catch (error) {}
  };

  const handleEdit = (client) => {
    setSelected(client);
    setForm({
      name: client.name || '',
      phone: client.phone || '',
      email: client.email || '',
      document: client.document || '',
      notes: client.notes || '',
      tags: (client.tags || []).join(', '),
      company_id: client.company_id || '',
    });
    loadHistory(client.id);
  };

  const handleDelete = async (clientId) => {
    try {
      await axios.delete(`${apiBaseUrl}/customers/${clientId}`);
      if (selected && selected.id === clientId) {
        setSelected(null);
        setHistory([]);
      }
      loadClients();
    } catch (error) {}
  };

  const handleLink = async () => {
    if (!selected) return;
    try {
      await axios.post(`${apiBaseUrl}/customers/link`, {
        customer_id: selected.id,
        chatId: linkForm.chatId || undefined,
        phone: linkForm.phone || undefined
      });
      setLinkForm({ chatId: '', phone: '' });
      loadHistory(selected.id);
    } catch (error) {}
  };

  const filteredClients = clients.filter((client) => {
    const text = [
      client.name,
      client.phone,
      client.email,
      client.document,
      ...(client.tags || [])
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return text.includes(searchTerm.toLowerCase());
  });

  return (
    <div className="crm-manager">
      <div className="crm-manager__header">
        <h2>Cadastro de Clientes</h2>
        <p>Gerencie dados pessoais, tags e histórico.</p>
      </div>

      <div className="crm-layout">
        <div className="crm-col">
          <div className="crm-panel">
            <div className="crm-panel__header">
              <h3>{selected ? 'Editar Cliente' : 'Novo Cliente'}</h3>
              {selected && (
                <button className="ghost" type="button" onClick={() => {
                  setSelected(null);
                  setForm({ name: '', phone: '', email: '', document: '', notes: '', tags: '', company_id: '' });
                  setHistory([]);
                }}>
                  Limpar seleção
                </button>
              )}
            </div>
            <form className="crm-form" onSubmit={handleSubmit}>
              <input name="name" placeholder="Nome completo" value={form.name} onChange={handleChange} required />
              <input name="phone" placeholder="Telefone" value={form.phone} onChange={handleChange} />
              <input name="email" placeholder="Email" value={form.email} onChange={handleChange} />
              <input name="document" placeholder="Documento (CPF/CNPJ)" value={form.document} onChange={handleChange} />
              <select name="company_id" value={form.company_id} onChange={handleChange}>
                <option value="">Empresa (opcional)</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
              <input name="tags" placeholder="Tags (separadas por vírgula)" value={form.tags} onChange={handleChange} />
              <textarea name="notes" placeholder="Observações" value={form.notes} onChange={handleChange} rows={3} />
              <button type="submit">{selected ? 'Atualizar Cliente' : 'Salvar Cliente'}</button>
            </form>
          </div>

          {selected && (
            <div className="crm-panel">
              <div className="crm-panel__header">
                <h3>Vincular Atendimento</h3>
              </div>
              <div className="crm-link__fields">
                <input
                  placeholder="chatId (ex: 5548...@c.us)"
                  value={linkForm.chatId}
                  onChange={(e) => setLinkForm({ ...linkForm, chatId: e.target.value })}
                />
                <span>ou</span>
                <input
                  placeholder="Telefone"
                  value={linkForm.phone}
                  onChange={(e) => setLinkForm({ ...linkForm, phone: e.target.value })}
                />
                <button type="button" onClick={handleLink}>Vincular</button>
              </div>
            </div>
          )}

          {selected && (
            <div className="crm-panel">
              <div className="crm-panel__header">
                <h3>Histórico do Cliente</h3>
              </div>
              <div className="crm-history__list">
                {history.length === 0 && <p className="crm-empty">Sem registros ainda.</p>}
                {history.map((item, idx) => (
                  <div key={`${item.timestamp}-${idx}`} className="crm-history__item">
                    <span className="crm-history__meta">{item.chatId} · {item.from}</span>
                    <p>{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="crm-col">
          <div className="crm-panel">
            <div className="crm-panel__header">
              <h3>Clientes</h3>
              <input
                className="crm-search"
                placeholder="Buscar por nome, telefone ou tag"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="crm-list">
              {filteredClients.length === 0 && (
                <div className="crm-empty">Nenhum cliente encontrado.</div>
              )}
              {filteredClients.map((client) => (
                <div key={client.id} className="crm-card">
                  <div className="crm-card__title">{client.name}</div>
                  <div className="crm-card__meta">{client.phone || 'Sem telefone'} · {client.email || 'Sem email'}</div>
                  <div className="crm-card__meta">Empresa: {client.company_name || 'Sem empresa'}</div>
                  <div className="crm-card__meta">{client.document || 'Sem documento'}</div>
                  <div className="crm-tags">
                    {(client.tags || []).map((tag) => (
                      <span key={tag} className="crm-tag">{tag}</span>
                    ))}
                  </div>
                  {client.notes && <p className="crm-card__notes">{client.notes}</p>}
                  <div className="crm-card__actions">
                    <button onClick={() => handleEdit(client)}>Editar</button>
                    <button className="ghost" onClick={() => handleDelete(client.id)}>Excluir</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientManager;
